/**
 * CareLogix™ AdminOne — LifeCommand™ Aggregate Dashboard (additive).
 *
 * PII-free aggregates only. No per-member identifiers. No raw biometric
 * timestreams. Surfaces:
 *   - LifeScore distribution status
 *   - FoodRx Coach engagement status
 *   - Wearable adoption + sync status
 *   - Truthful NO_DATA_AVAILABLE states
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

const _tok = () => { try { return window.localStorage.getItem("carelogix_adminone_token") || ""; } catch { return ""; } };

async function _adminGet(p) {
  try {
    const t = _tok();
    if (!t) return null;
    const r = await fetch(`${BACKEND}${p}`, { headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

const S = {
  page: { minHeight: "100vh", padding: "32px 40px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 },
  card: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: 16 },
  cardLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  cardValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, marginTop: 8 },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 10px", border: "1px solid", borderRadius: 999 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20, marginTop: 18 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  note: { padding: "10px 14px", background: "rgba(124,200,96,0.06)", border: "1px solid rgba(124,200,96,0.30)", borderRadius: 10, color: "#7AC860", fontSize: 12, marginTop: 16 },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
};

function Pill({ children, color }) {
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

export default function AdminOneLifeCommand() {
  const [lifescore, setLifescore] = useState(null);
  const [wearables, setWearables] = useState(null);
  const [dash, setDash]           = useState(null);
  const _once = useRef(false);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    (async () => {
      const [ls, wr, db] = await Promise.all([
        _adminGet("/api/adminone/lifescore/summary"),
        _adminGet("/api/adminone/wearables/summary"),
        _adminGet("/api/adminone/lifecommand/dashboard"),
      ]);
      setLifescore(ls); setWearables(wr); setDash(db);
    })();
  }, []);

  const widgets = (dash && dash.widgets) || [];
  const lsStatus = lifescore && lifescore.lifescore_distribution;
  const wrAdoption = wearables && wearables.adoption;
  const wrSync = wearables && wearables.sync_status;

  return (
    <main style={S.page} data-testid="adminone-lifecommand-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ADMINONE · ORACLE LIFECOMMAND™ DASHBOARD</div>
          <h1 style={S.title}>LifeCommand Daily Habit Intelligence — Aggregate</h1>
          <p style={S.sub}>
            PII-free population aggregates only. ORACLE never surfaces per-member identifiers, raw biometric streams, or token material on AdminOne.
            <strong> Decision-support only. No diagnosis. No treatment.</strong>
          </p>
        </div>
        <div>
          <Link to="/adminone/telemetry" style={S.link} data-testid="link-telemetry">← Enterprise Telemetry</Link>
          <Link to="/adminone/executive-intelligence" style={S.link} data-testid="link-execintel">Executive Intelligence →</Link>
          <Link to="/adminone/foodrx-intelligence" style={S.link} data-testid="link-foodrx">FoodRx Intelligence →</Link>
        </div>
      </header>

      <section data-testid="adminone-lifecommand-kpis">
        <div style={S.grid}>
          <div style={S.card} data-testid="kpi-lifescore">
            <div style={S.cardLabel}>LIFESCORE DISTRIBUTION</div>
            <div style={S.cardValue}>
              {lsStatus === "NO_DATA_AVAILABLE" ? <Pill color="#fcd34d">NO_DATA_AVAILABLE</Pill> : <Pill color="#7AC860">ACTIVE</Pill>}
            </div>
          </div>
          <div style={S.card} data-testid="kpi-wearable-adoption">
            <div style={S.cardLabel}>WEARABLE ADOPTION</div>
            <div style={S.cardValue}>
              {wrAdoption === "NO_DATA_AVAILABLE" ? <Pill color="#fcd34d">NO_DATA_AVAILABLE</Pill> : <Pill color="#7AC860">ACTIVE</Pill>}
            </div>
          </div>
          <div style={S.card} data-testid="kpi-wearable-sync">
            <div style={S.cardLabel}>WEARABLE SYNC STATUS</div>
            <div style={S.cardValue}>
              {wrSync === "NO_DATA_AVAILABLE" ? <Pill color="#fcd34d">NO_DATA_AVAILABLE</Pill> : <Pill color="#7AC860">ACTIVE</Pill>}
            </div>
          </div>
          <div style={S.card} data-testid="kpi-privacy">
            <div style={S.cardLabel}>PRIVACY MODE</div>
            <div style={S.cardValue}><Pill color="#7AC860">AGGREGATE_ONLY</Pill></div>
          </div>
        </div>
      </section>

      <section style={S.section} data-testid="adminone-lifecommand-widgets">
        <h2 style={S.sectionTitle}>Aggregate widgets</h2>
        <p style={S.sectionSub}>{(dash && dash.scope) ? `Scope: ${dash.scope}` : "—"}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {widgets.map((w) => (
            <div key={w} style={{ ...S.card, padding: "10px 14px" }} data-testid={`widget-${w.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div style={S.cardLabel}>{w}</div>
              <div style={{ ...S.cardValue, fontSize: 12, marginTop: 6 }}>
                <Pill color="#fcd34d">NO_DATA_AVAILABLE</Pill>
              </div>
            </div>
          ))}
        </div>
        {widgets.length === 0 && <p style={{ color: "#fcd34d", fontFamily: "'Space Mono', monospace", fontSize: 11 }}>NO_DATA_AVAILABLE — sign in to AdminOne to load aggregates.</p>}
      </section>

      <div style={S.note} data-testid="privacy-note">
        Privacy: ORACLE AdminOne aggregates strip every per-member identifier, access token, refresh token, and raw biometric reading before serving. Aggregate counts only.
      </div>

      <div style={S.compliance} data-testid="adminone-lifecommand-compliance">
        {(dash && dash.disclaimer) || "Decision-support only. Not medical advice. ORACLE LifeCommand™ aggregates do not diagnose, treat, prescribe, or replace clinician care."}
      </div>
    </main>
  );
}
